/**
 * A save refused because someone else saved the record first (409 `STALE_EDIT`).
 *
 * The server sends who and when, never the record itself: the screen re-reads
 * through its own read, which applies the reader's field masking. The rule is
 * docs/architecture/concurrent-edits.md.
 */
export interface StaleEdit {
  currentVersion: number;
  /** ISO instant of the save that moved the record on. */
  changedAt: string;
  /** Display name of whoever saved; null for a system actor or when unknown. */
  changedBy: string | null;
  /** The save was the reader's own, from another tab or window. */
  byYou: boolean;
}

export const STALE_EDIT = 'STALE_EDIT';

/** Reads the refusal's details from a 409 body; undefined for any other body. */
export function parseStaleEdit(body: unknown): StaleEdit | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const b = body as Record<string, unknown>;
  if (b.code !== STALE_EDIT) return undefined;
  return {
    currentVersion: typeof b.currentVersion === 'number' ? b.currentVersion : 0,
    changedAt: typeof b.changedAt === 'string' ? b.changedAt : new Date().toISOString(),
    changedBy: typeof b.changedBy === 'string' && b.changedBy ? b.changedBy : null,
    byYou: b.byYou === true,
  };
}

/** The refusal carried by an error thrown from `makeApiCallJson`, or null. */
export function staleEditOf(error: unknown): StaleEdit | null {
  const detail = (error as { staleEdit?: StaleEdit } | null)?.staleEdit;
  return detail ?? null;
}

/**
 * "Alex Kim", "You in another tab" or "Someone else": a name for the subject of
 * a sentence, with no punctuation of its own, so the caller sets the commas.
 */
export function staleEditWho(edit: StaleEdit): string {
  if (edit.byYou) return 'You in another tab';
  return edit.changedBy ?? 'Someone else';
}

/** The possessive for "Use …'s": "Alex Kim's", "the other tab's", "theirs". */
export function staleEditTheirs(edit: StaleEdit): string {
  if (edit.byYou) return 'the other tab’s';
  return edit.changedBy ? `${edit.changedBy}’s` : 'theirs';
}

/** "14:32" for a save today, otherwise "14 Oct, 14:32" (en-GB). */
export function formatChangedAt(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  const time = at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const sameDay =
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate();
  if (sameDay) return time;
  return `${at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}, ${time}`;
}

/**
 * One sentence for surfaces that do not merge (a drag, a list row action):
 * "Alex Kim changed this allocation at 14:32. It's up to date now."
 */
export function staleEditSentence(edit: StaleEdit, what: string): string {
  const when = formatChangedAt(edit.changedAt);
  if (edit.byYou) return `You changed ${what} in another tab at ${when}. It’s up to date now.`;
  return `${edit.changedBy ?? 'Someone else'} changed ${what} at ${when}. It’s up to date now.`;
}
